import type { Role } from "../../types/user";

export interface Member {
  id: string;
  email: string;
  emailVerified: boolean;
  role: Role;
  joinedAt: string;
}

interface MemberListProps {
  members: Member[];
  currentUserRole: Role;
  organizationCreatorId: string;
  onRoleChange?: (userId: string, newRole: Role) => Promise<void>;
  onRemoveMember?: (userId: string) => Promise<void>;
}

const roleColors = {
  owner: "bg-blue-100 text-blue-800",
  admin: "bg-yellow-100 text-yellow-800",
  member: "bg-gray-100 text-gray-800",
};

export function MemberList({
  members,
  currentUserRole,
  organizationCreatorId,
  onRoleChange,
  onRemoveMember,
}: MemberListProps) {
  const canManageMembers = currentUserRole === "owner" || currentUserRole === "admin";

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Joined
            </th>
            {canManageMembers && (
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {members.map((member) => {
            const isCreator = member.id === organizationCreatorId;
            const canChangeRole = canManageMembers && !isCreator;
            const canRemove = canManageMembers && !isCreator;

            return (
              <tr key={member.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {member.email}
                  {isCreator && <span className="ml-2 text-xs text-gray-500">(Creator)</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      roleColors[member.role]
                    }`}
                  >
                    {member.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {member.emailVerified ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Verified
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(member.joinedAt)}
                </td>
                {canManageMembers && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canChangeRole && onRoleChange && (
                      <select
                        value={member.role}
                        onChange={(e) =>
                          onRoleChange(member.id, (e.target as HTMLSelectElement).value as Role)
                        }
                        className="mr-2 text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        disabled={currentUserRole === "admin" && member.role === "owner"}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        {currentUserRole === "owner" && <option value="owner">Owner</option>}
                      </select>
                    )}
                    {canRemove && onRemoveMember && (
                      <button
                        onClick={() => onRemoveMember(member.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
